import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { changePassword } from '@/api/auth';
import { useAuth } from '@/lib/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const schema = z
  .object({
    oldPassword: z.string().min(1, 'Required'),
    password: z.string().min(12, 'Password must be at least 12 characters'),
    confirm: z.string().min(1, 'Please confirm your password'),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });
type Values = z.infer<typeof schema>;

export function SetPassword() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  async function onSubmit(values: Values) {
    setError(null);
    try {
      await changePassword(values.oldPassword, values.password);
      const user = useAuth.getState().user;
      if (user) {
        useAuth.getState().setUser({ ...user, must_change_password: false });
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password.');
    }
  }

  return (
    <section className="mx-auto w-full max-w-md px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <Card>
        <CardHeader>
          <CardTitle>Please set a new password</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            You were invited with a temporary password. Set a new one to continue.
          </p>
          <form className="space-y-4" noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-1">
              <Label htmlFor="oldPassword">Temporary password</Label>
              <Input
                id="oldPassword"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.oldPassword != null}
                aria-describedby={errors.oldPassword ? 'oldPassword-error' : undefined}
                {...register('oldPassword')}
              />
              {errors.oldPassword && (
                <p id="oldPassword-error" role="alert" className="text-xs text-destructive">
                  {errors.oldPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                aria-invalid={errors.password != null}
                aria-describedby={errors.password ? 'password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p id="password-error" role="alert" className="text-xs text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                aria-invalid={errors.confirm != null}
                aria-describedby={errors.confirm ? 'confirm-error' : undefined}
                {...register('confirm')}
              />
              {errors.confirm && (
                <p id="confirm-error" role="alert" className="text-xs text-destructive">
                  {errors.confirm.message}
                </p>
              )}
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save new password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
